import React, { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";

export function Card({ children, className = "", testId }) {
  return <div data-testid={testId} className={`rounded-2xl border border-border bg-card p-5 shadow-md shadow-black/20 ${className}`}>{children}</div>;
}

const STATUS_KEYS = ["status", "result", "severity", "risk_level", "health", "retest_status", "assurance_status"];

export default function ModulePage({ title, subtitle, endpoint, columns, dataKey, searchKeys = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let ok = true;
    setLoading(true);
    api.get(endpoint).then(({ data }) => {
      if (!ok) return;
      const list = dataKey ? data[dataKey] : data;
      setRows(Array.isArray(list) ? list : []);
    }).catch(() => setRows([])).finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [endpoint, dataKey]);

  const filtered = q
    ? rows.filter((r) => (searchKeys.length ? searchKeys : Object.keys(r)).some((k) => String(r[k] ?? "").toLowerCase().includes(q.toLowerCase())))
    : rows;

  const raContext = `${title}. ${rows.length} records. ` + filtered.slice(0, 8).map((r) => columns.map((c) => `${c.label}: ${r[c.key]}`).join(", ")).join(". ");

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} readAloudContext={raContext} />
      <Card>
        <div className="mb-4 flex items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="pointer-events-none absolute inset-y-0 my-auto h-4 w-4 text-muted-foreground" style={{ insetInlineStart: "0.75rem" }} />
            <input data-testid={`${title.toLowerCase().replace(/\W+/g, "-")}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              className="w-full rounded-xl border border-border bg-background py-2 ps-9 pe-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <span className="text-xs text-muted-foreground">{filtered.length} / {rows.length}</span>
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-start text-xs uppercase tracking-wider text-muted-foreground">
                  {columns.map((c) => <th key={c.key} className="px-3 py-2 text-start font-semibold">{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} data-testid={`row-${i}`} className="border-b border-border/50 hover:bg-secondary/40">
                    {columns.map((c) => (
                      <td key={c.key} className="px-3 py-2.5 align-top">
                        {STATUS_KEYS.includes(c.key) && r[c.key] ? <StatusBadge value={r[c.key]} /> : <span className="text-foreground/90">{Array.isArray(r[c.key]) ? r[c.key].join(", ") : String(r[c.key] ?? "—")}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={columns.length} className="py-10 text-center text-muted-foreground">No records</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
